import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';

import { Observable, Subscription, combineLatest, filter, map, take } from 'rxjs';
import { Site } from 'src/app/model/site';
import { Scenery } from 'src/app/model/scenery';
import { selectSceneryActions, selectSceneryAdded, selectSceneryAdding, selectSceneryEdited, selectSceneryEditing, selectStepAdded, selectStepEdited } from 'src/app/store/scenery/scenery.selector';
import { Device } from 'src/app/model/device';
import { SCENERY_ACTION_TYPES, WAIT_TIME_UNITS } from 'src/app/conf/scenery';
import { SceneryActionType } from 'src/app/model/scenery-action-type';
import { SceneryAction } from 'src/app/model/scenery-action';
import { SceneryStep } from 'src/app/model/scenery-step';
import { SettingsCategory } from 'src/app/model/settings-category';
import { ControlItem } from 'src/app/model/control-item';
import { Command, ConnectedAutomationsCommand } from 'src/app/model/command';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EDeviceCategory } from 'src/app/conf/device-categories';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { selectConnectedAutomationsCommands, selectCurrentDeviceCommands, selectCurrentDeviceSettingsCategories } from 'src/app/store/device/device.selector';
import { ECommands } from 'src/app/conf/commands';
import { Setting } from 'src/app/model/setting';
import * as SceneryActions from 'src/app/store/scenery/scenery.actions';
import { StandardDynamicComponent, StandardNewValidator, StandardNewValidatorStepTabs, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	templateUrl: './step.dialog.component.html',
	styles: `
		#stepDialog {
			min-height: 20vw;
		}
	`
})
export class StepDialogComponent implements OnInit, OnDestroy {
	added$: Observable<number | undefined>;
	addedSub: Subscription | undefined;
	adding$: Observable<boolean | undefined>;
	addingSub: Subscription | undefined;
	edited$: Observable<boolean | undefined>;
	editedSub: Subscription | undefined;
	editing$: Observable<boolean | undefined>;
	editingSub: Subscription | undefined;

	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;

	commands$: Observable<Command[] | undefined>;
	commandsSub: Subscription | undefined;
	connectedAutomationsCommands$: Observable<ConnectedAutomationsCommand[] | undefined>;
	connectedAutomationsCommandsSub: Subscription | undefined;

	settings$: Observable<SettingsCategory[] | undefined>;
	settingsSub: Subscription | undefined;
	stepAdded$: Observable<boolean | undefined>;
	stepAddedSub: Subscription | undefined;
	stepEdited$: Observable<boolean | undefined>;
	stepEditedSub: Subscription | undefined;

	stepNameForm: FormGroup;

	added: number;
	adding: boolean;
	edited: boolean;
	editing: boolean;

	devices: Device[] = [];
	device: Device;

	actionTypes: SceneryActionType[] = SCENERY_ACTION_TYPES;

	scenery: Scenery;
	step: SceneryStep;
	action: SceneryAction;

	sceneryActions$: Observable<SceneryAction[] | undefined>;
	sceneryActionsSub: Subscription | undefined;

	timeUnits = WAIT_TIME_UNITS;

	settingId: number;
	settings: SettingsCategory[];
	settingsCache: any = {};
	controlItem: ControlItem = new ControlItem();

	commands: Command[];
	connectedAutomationsCommands: ConnectedAutomationsCommand[] = [];

	addStepSub: Subscription;

	items: MenuItem[] | undefined;
	activeIndex: number = 0;

	addScenary: boolean;

	filteredDevices: any[];

	// design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService
	) {
		this.addScenary = (config.header === this.translate.instant('ADD_STEP'));

		if (config.data !== undefined && config.data.scenery !== undefined) {
			this.scenery = config.data.scenery;
		}
		this.step = new SceneryStep(this.scenery.Id);
		if (config.data !== undefined && config.data.step !== undefined) {
			this.step = config.data.step;
		}
		this.action = new SceneryAction();
		this.initSelectors();
	}

	ngOnInit(): void {
		this.store.dispatch(SceneryActions.getActions({}));
		this.buildForm();
		this.subscribeAll();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);

		StandardNewValidatorStepTabs.firstTabfields = [];
		StandardNewValidatorStepTabs.secondTabfields = [];
		StandardNewValidatorStepTabs.thirdTabfields = [];

		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.device, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.actionType, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.command, 2);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.delayTime, 2);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.delayUnit, 2);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.settingId, 2);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.settingValue, 2);
	}

	loadItemsStep(): void {
		this.items = [
			{ label: (this.onExtendedMobile()) ? '' : this.translate.instant('SELECT_DEVICE') },
			{ label: (this.onExtendedMobile()) ? '' : this.translate.instant('ACTION_TYPE') },
			{ label: (this.onExtendedMobile()) ? '' : this.translate.instant('SELECT_ACTION') }
		];
	}

	buildForm(): void {
		this.stepNameForm = new FormGroup({
			'name': new FormControl(this.step.Name, Validators.required),
			'device': new FormControl(null, Validators.required),
			'actionType': new FormControl(null, Validators.required),
			'command': new FormControl(null, Validators.required),
			'delayTime': new FormControl(this.action.WaitMs, [Validators.required, Validators.pattern('[0-9]+')]),
			'delayUnit': new FormControl(WAIT_TIME_UNITS[0], Validators.required),
			'settingId': new FormControl(this.action.SettingId, Validators.required),
			'settingValue': new FormControl(this.action.SettingValue, Validators.required),
		});
		const actionType = this.getActionType(this.action);
		this.f.actionType.value = actionType;
		this.setupActionForm(this.f.actionType.value.Id);
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.added$ = this.store.select(selectSceneryAdded);
		this.adding$ = this.store.select(selectSceneryAdding);
		this.edited$ = this.store.select(selectSceneryEdited);
		this.editing$ = this.store.select(selectSceneryEditing);
		this.commands$ = this.store.select(selectCurrentDeviceCommands);
		this.settings$ = this.store.select(selectCurrentDeviceSettingsCategories);
		this.stepAdded$ = this.store.select(selectStepAdded);
		this.stepEdited$ = this.store.select(selectStepEdited);
		this.connectedAutomationsCommands$ = this.store.select(selectConnectedAutomationsCommands);
		this.sceneryActions$ = this.store.select(selectSceneryActions)
	}

	setupActionType(): void {
		const actionType = this.getActionType(this.action);
		if (this.f.actionType !== undefined) {
			this.f.actionType.value = actionType;
		}
		this.setupActionForm(actionType.Id);
	}

	selectCurrentCommand() {
		// checks for avoid race conditions
		if (!this.action || !this.action.CommandId || !this.commands) {
			return;
		}

		// this snippet load on edit dialog the prev command associated
		const commandToSelect = this.getCommand(this.action.CommandId);
		if (commandToSelect) {
			setTimeout(() => {
				this.f.command.setValue(commandToSelect);
			});
		}
	}

	subscribeAll(): void {
		this.siteSub = this.site$.pipe(map(site => site.Devices.filter(d => this.isAutoVirtual(d))))
			.subscribe(devices => {
				this.devices = devices;
				this.device = this.selectDevice(this.step.DeviceId);
				this.f.device.value = this.device;
				this.loadDeviceData(this.device);
			});
		combineLatest([
			this.commands$.pipe(
				filter(commands => !!commands), // no undefined or null
				map(commands => commands.filter(c => ![ECommands.openclose, ECommands.sequential].includes(c.Id)))
			),
			this.connectedAutomationsCommands$.pipe(
				filter(connected => !!connected) // no undefined or null
			)
		]).subscribe(([commands, connectedCommands]) => {
			let allCommands = [...commands]; // we have for sure at least standard commands

			if (connectedCommands && connectedCommands.length > 0) { // connected automation commands (for ex. under RBEPLUS)
				this.connectedAutomationsCommands = connectedCommands;
				const automCommands = connectedCommands
					.flatMap(automation => automation.Commands ?? [])
					.filter(c => ![ECommands.openclose, ECommands.sequential].includes(c.Id));
				allCommands = allCommands.concat(automCommands);
			} else {
				this.connectedAutomationsCommands = [];
			}

			this.commands = allCommands; // this.commands is the final array of dropdown commands available for device selected

			this.selectCurrentCommand();
		});
		this.settingsSub = this.settings$.pipe(filter(dsc => !!dsc)).subscribe((settingsCategories) => {
			this.settings = settingsCategories;
			this.fillDeviceSettings(settingsCategories);
			if (this.action.SettingId) {
				this.initControlItem(this.settingsCache[this.action.SettingId]);
			}
		});
		this.stepAddedSub = this.stepAdded$?.pipe(filter(add => !!add))
			.subscribe(() => { this.closeDialog(); });
		this.stepEditedSub = this.stepEdited$?.pipe(filter(edit => !!edit))
			.subscribe(() => { this.closeDialog(); });
		this.sceneryActionsSub = this.sceneryActions$?.pipe(
			map(actions => actions.find(a => a.StepId === this.step.Id)),
			filter(action => !!action), // we don't need undefined or null action
			take(1) // take the first one and avoid redundant recall
		).subscribe(
			(action) => {
				this.action = action;
				this.setupActionType();
				this.selectCurrentCommand();
			}
		);
	}

	ngOnDestroy(): void {
		this.siteSub?.unsubscribe();
		this.addedSub?.unsubscribe();
		this.addingSub?.unsubscribe();
		this.editedSub?.unsubscribe();
		this.editingSub?.unsubscribe();
		this.commandsSub?.unsubscribe();
		this.settingsSub?.unsubscribe();
		this.stepAddedSub?.unsubscribe();
		this.stepEditedSub?.unsubscribe();
		this.connectedAutomationsCommandsSub?.unsubscribe();
		this.sceneryActionsSub?.unsubscribe();
	}

	public findInvalidControls() {
		const invalid = [];
		const controls = this.stepNameForm.controls;
		for (const name in controls) {
			if (controls[name].invalid) {
				invalid.push(name);
			}
		}
		return invalid;
	}

	isAutoVirtual(device: Device): boolean {
		return device.CategoryId === EDeviceCategory.Automation || device.CategoryId === EDeviceCategory.Virtual;
	}

	selectDevice(deviceId: number): Device {
		if (this.devices.length === 0) {
			return null;
		}
		if (!deviceId) {
			return null;
		}
		return this.devices.find(d => d.Id === deviceId);
	}

	loadDeviceData(device: Device): void {
		if (!device) {
			return;
		}
		this.filterActionTypes(device);
		this.loadDeviceModelSettings(device);
		this.loadDeviceCommands(device);
	}

	filterActionTypes(device): void {
		this.actionTypes = SCENERY_ACTION_TYPES.filter(at => at.Target.includes(device.CategoryId));
	}

	loadDeviceModelSettings(device): void {
		if (device.CategoryId === EDeviceCategory.Virtual) {
			return;
		}
		this.store.dispatch(DeviceActions.getDeviceModelSettings({ modelId: device.ModelId }));
	}

	loadDeviceCommands(device: Device): void {
		this.store.dispatch(DeviceActions.getCommands({ device }));
	}

	getActionType(action: SceneryAction): SceneryActionType {
		if (action !== undefined) {
			if (action.CommandId) {
				return this.actionTypes[0];
			} else if (action.WaitMs) {
				return this.actionTypes[1];
			} else if (action.SettingId) {
				return this.actionTypes[2];
			} else {
				return this.actionTypes[0];
			}
		}
		return this.actionTypes[0];
	}

	getCommand(command: number): Command {
		if (this.commands === undefined) {
			return null;
		}
		return this.commands.find(comm => comm.Id === command);
	}

	getCommandDescription(commandId: number): string {
		if (!this.commands) {
			return '';
		}
		return this.commands.find(c => c.Id === commandId).Description;
	}

	onSelectDevice() {
		this.loadDeviceData(this.f.device.value);
	}

	onSelectDeviceAction() {
		this.loadDeviceData(this.f.device.value);
	}

	onSelectTypeAction() {
		this.setupActionForm(this.f.actionType.value.Id);
	}

	onSelectRadioButton() {
		const setting: Setting = this.settingsCache[this.f.settingId.value];
		this.initControlItem(setting);
	}

	setupActionForm(actionTypeId: number): void {
		switch (actionTypeId) {
			case 1:
				this.setupCommandActionForm();
				break;
			case 2:
				this.setupWaitActionForm();
				break;
			case 3:
				this.setupSettingActionForm();
				break;
		}
	}

	setupCommandActionForm(): void {
		const command = this.getCommand(this.action.CommandId);
		this.f.command.value = command;
		this.stepNameForm.get('command').setValidators([Validators.required]);
		this.stepNameForm.get('delayTime').clearValidators();
		this.stepNameForm.get('delayUnit').clearValidators();
		this.stepNameForm.get('settingId').clearValidators();
		this.stepNameForm.get('settingValue').clearValidators();
		this.stepNameForm.get('command').updateValueAndValidity();
		this.stepNameForm.get('delayTime').updateValueAndValidity();
		this.stepNameForm.get('delayUnit').updateValueAndValidity();
		this.stepNameForm.get('settingId').updateValueAndValidity();
		this.stepNameForm.get('settingValue').updateValueAndValidity();
	}

	setupWaitActionForm(): void {
		this.f.delayTime.value = this.action.WaitMs;
		this.f.delayUnit.value = WAIT_TIME_UNITS[0];
		this.stepNameForm.get('command').clearValidators();
		this.stepNameForm.get('delayTime').setValidators([Validators.required, Validators.pattern('[0-9]+')]);
		this.stepNameForm.get('delayUnit').setValidators([Validators.required]);
		this.stepNameForm.get('settingId').clearValidators();
		this.stepNameForm.get('settingValue').clearValidators();
		this.stepNameForm.get('command').updateValueAndValidity();
		this.stepNameForm.get('delayTime').updateValueAndValidity();
		this.stepNameForm.get('delayUnit').updateValueAndValidity();
		this.stepNameForm.get('settingId').updateValueAndValidity();
		this.stepNameForm.get('settingValue').updateValueAndValidity();
	}

	setupSettingActionForm(): void {
		/* this.actionForm = this.fb.group({
		 settingId: [this.action.SettingId, Validators.required],
		 settingValue: [this.action.SettingValue, Validators.required],
	 });*/
		this.setControlItem();
		this.stepNameForm.get('command').clearValidators();
		this.stepNameForm.get('delayTime').clearValidators();
		this.stepNameForm.get('delayUnit').clearValidators();
		this.stepNameForm.get('settingId').setValidators([Validators.required]);
		this.stepNameForm.get('settingValue').setValidators([Validators.required]);
		this.stepNameForm.get('command').updateValueAndValidity();
		this.stepNameForm.get('delayTime').updateValueAndValidity();
		this.stepNameForm.get('delayUnit').updateValueAndValidity();
		this.stepNameForm.get('settingId').updateValueAndValidity();
		this.stepNameForm.get('settingValue').updateValueAndValidity();
	}

	setControlItem(): void {
		if (this.action.SettingId) {
			this.initControlItem(this.settingsCache[this.action.SettingId]);
		}
	}

	fillDeviceSettings(settingsCategories): void {
		for (const category of settingsCategories) {
			for (const setting of category.Settings) {
				this.settingsCache[setting.Id] = setting;
			}
		}
	}

	initControlItem(setting: Setting): void {
		if (setting === undefined) {
			return;
		}
		this.controlItem = setting.ControlItem;
		this.f.settingId.value = setting.Id;
		if (setting.ControlItem.Type === 'slider') {
			this.f.settingValue.setValue(0);
			if (!setting.ControlItem.StepSize) {
				setting.ControlItem.StepSize = 1;
			}
			if (!setting.ControlItem.DisplayScale) {
				setting.ControlItem.DisplayScale = 1;
			}

			if (this.action && this.action.SettingValue) {
				// required since initControlItem is always called AFTER buildform
				this.f.settingValue.value = this.getSettingScaledValue(this.action.SettingValue);
			} else {
				this.f.settingValue.value = this.getSliderScaledMin();
			}
		} else {
			const val = this.controlItem.Items.find(ec => ec.Value === this.action.SettingValue);
			this.f.settingValue.value = val;
		}
	}


	getSettingScaledValue(value: number): number {
		if ((this.controlItem.Type === 'slider') && (this.controlItem.DisplayScale !== 1)) {
			return this.scaledValueFloat(value, this.controlItem.DisplayScale);
		}
		return value;
	}

	getSliderScaledMin(): number {
		return this.scaledValueFloat(this.controlItem.Min, this.controlItem.DisplayScale);
	}

	getSliderScaledMax() {
		return this.scaledValueFloat(this.controlItem.Max, this.controlItem.DisplayScale);
	}

	getSliderScaledStep() {
		return this.scaledValueFloat(this.controlItem.StepSize, this.controlItem.DisplayScale);
	}

	filterDevice(event) {
		const filtered: any[] = [];
		const query = event.query;
		if (this.devices) {
			for (let i = 0; i < this.devices.length; i++) {
				const device = this.devices[i];
				if (device.Name.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
					filtered.push(device);
				}
			}
		}
		this.filteredDevices = filtered;
	}

	formattedSliderMin(): string {
		return this.scaledValueString(this.controlItem.Min, this.controlItem.DisplayScale);
	}

	formattedSliderMax(): string {
		return this.scaledValueString(this.controlItem.Max, this.controlItem.DisplayScale);
	}

	scaledValueString(value: number, scale: number): string {
		return this.getValueString(value * scale, scale).replace(".", ",");
	}

	scaledValueFloat(value: number, scale: number): number {
		return this.getValueFloat(value * scale);
	}

	getValueString(value: number, scale: number): string {
		if (Math.floor(scale) === 1) {
			return value.toFixed(0);
		}
		return value.toFixed(1);
	}

	getValueFloat(value: number): number {
		return parseFloat(value.toFixed(1));
	}

	settingOwner(categorySettings: Setting[], settingId): boolean {
		return categorySettings.map(s => s.Id).includes(settingId.value);
	}

	setSliderValue(settingValue: any, value: number): void {
		let newValue = settingValue.value / this.controlItem.DisplayScale + value * this.controlItem.StepSize;
		newValue = this.getValueFloat(newValue);
		if (newValue < this.controlItem.Min) {
			return;
		}
		if (newValue > this.controlItem.Max) {
			return;
		}
		// const oldValue = this.f[setting.Id].value;
		newValue = this.scaledValueFloat(newValue, this.controlItem.DisplayScale);
		settingValue.setValue(newValue);
		settingValue.markAsDirty();
	}

	onChangeSlider(settingValue: any) {
		settingValue.setValue(settingValue.value);
	}

	onChangeInputSlider(settingValue: any) {
		settingValue.setValue(settingValue.value);
	}

	canSwitchTab(): boolean {
		return StandardNewValidatorStepTabs.canSwitchTab(this.activeIndex);
	}

	changeActiveTabClick(index: number) {
		// Disabilitato blocco cambio tab (if canSwitchTab())
		this.activeIndex = index;
	}

	getAutomationName(command: Command): string {
		const connectedAutomations = this.f.device.value?.ConnectedAutomations;
		if (!connectedAutomations?.length || !this.connectedAutomationsCommands?.length) {
			return '';
		}

		const connectedAutomationCommands = this.connectedAutomationsCommands.find(connAutom =>
			connAutom.Commands?.some(connAutCmd => connAutCmd.Id === command.Id)
		);
		if (!connectedAutomationCommands) {
			return '';
		}

		const currentConnectedAutomation = connectedAutomations.find(autom =>
			autom.CrpItemId === connectedAutomationCommands.CrpItemId
		);
		if (!currentConnectedAutomation) {
			return '';
		}

		return currentConnectedAutomation.Name + ' - ' + currentConnectedAutomation.CrpClassName + ' - ';
	}

	getOutputName(command: Command): string {
		if (command.OutputId) {
			const outId : number = command.OutputId;
			switch (outId) {
				case 1:
					return 'OUT 1 - ';
				case 2:
					return 'OUT 2 - ';
				default:
					return '';
			}
		} else {
			return '';
		}
	}

	getColorSettingValue(settValue): string {
		if (settValue !== undefined) {
			const color = settValue.Color
			switch (color) {
				case 'white':
					return '#ffffff';
				case 'yellow':
					return '#ffc107';
				case 'orange':
					return '#ff6900';
				case 'red':
					return '#f44336';
				case 'violet':
					return '#9c27b0';
				case 'blue':
					return '#03a9f4';
				case 'lightblue':
					return '#00bcd4';
				case 'green':
					return '#4caf50';
				default:
					return '';
			}
		}
		return '';
	}

	enableConfirmButtons(): boolean {
		return this.activeIndex > 1;
	}

	get f(): any {
		return this.stepNameForm.controls;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.stepNameForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.stepNameForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	buildDescription(): string {
		let desc: string;
		let text: string;
		switch (this.f.actionType.value.Id) {
			case 1:
				desc = this.getCommandDescription(this.f.command.value.Id);
				break;
			case 2:
				desc = `${this.f.delayTime.value} ${this.f.delayUnit.value.Label}`;
				break;
			case 3:
				if (Number.isFinite(+this.f.settingValue.value)) {
					text = ': ' + this.f.settingValue.value;
				} else {
					text = ': ' + this.f.settingValue.value.Description;
				}
				desc = this.settingsCache[this.f.settingId.value].Description + ' ' + text;
				break;
		}
		return desc;
	}

	nextIndex(): number {
		// add index field for retro compatibility with already created steps which index doesn't start from 0
		let index = 0;
		if (this.scenery.Steps && this.scenery.Steps.length > 0) {
			const orderedSteps = this.scenery.Steps.sort((a, b) => a.Index - b.Index);
			index = orderedSteps[orderedSteps.length - 1].Index + 1;
		}
		return index;
	}

	buildStep(): void {
		this.step.Name = this.f.name.value;
		this.step.Description = this.buildDescription();
		this.step.DeviceId = this.f.device.value.Id;
		if (!this.step.Id) {
			this.step.Index = this.nextIndex();
		}
	}

	getDelayMs(): number {
		return this.f.delayTime.value * this.f.delayUnit.value.Scale;
	}

	getSettingNormalizedValue(value: number): number {
		if ((this.controlItem.Type === 'slider') && (this.controlItem.DisplayScale !== 1)) {
			return this.getValueFloat(value / this.controlItem.DisplayScale);
		}
		return value;
	}

	buildAction(): void {
		const action = {
			...this.action,
			SettingId: null,
			SettingValue: null,
			WaitMs: null,
			CommandId: null,
		};
		const actionTypeId = this.f.actionType.value.Id;
		switch (actionTypeId) {
			case 1: // execute command
				action.CommandId = this.f.command.value.Id;
				break;
			case 2: // wait delay
				action.WaitMs = this.getDelayMs();
				break;
			case 3: // apply configuration
				action.SettingId = this.f.settingId.value;
				if (Number.isFinite(+this.f.settingValue.value)) {
					action.SettingValue = +this.getSettingNormalizedValue(this.f.settingValue.value);
				} else {
					action.SettingValue = this.f.settingValue.value.Value;
				}
				break;
		}
		this.action = { ...action };
	}

	save(): void {
		this.buildStep();
		this.buildAction();
		if (this.step.Id) {
			this.store.dispatch(SceneryActions.editStep({ step: this.step, action: this.action }));
		} else {
			this.store.dispatch(SceneryActions.addStep({ step: this.step, action: this.action }));
		}
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
		this.loadItemsStep();
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicAutocomplete(): string {
		return StandardDynamicComponent.dynamicAutocomplete(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	// Errors management & Design Border | Input
	showErrorInvalidMessage(type: StepDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	newShowErrors(type: StepDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	getColorFirstTab(): string {
		this.canSwitchTab();
		return StandardNewValidatorStepTabs.getColorFirstTab();
	}

	getColorSecondTab(): string {
		return StandardNewValidatorStepTabs.getColorSecondTab(this.activeIndex);
	}

	getColorThirdTab(): string {
		return StandardNewValidatorStepTabs.getColorThirdTab(this.activeIndex);
	}

	showErrorsWithTabValidation(type: StepDialogComponent['f'], classType: string): string {
		return StandardNewValidatorStepTabs.newShowErrorsWithTabValidation(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: StepDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showErrorRequiredMessageMultiOptions(type: StepDialogComponent['f']): boolean {
		return type.errors !== null && !StandardNewValidator.getFirstCompile();
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	dynamicBorderLabelMobile(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}
